import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import {
  ProductFeature,
  ProductFeatureListResponse,
  ProductFeaturePayload,
  apiService,
} from '../services/api';
import ConfirmModal from './ConfirmModal';

interface ProductFeatureManagementProps {
  onBack?: () => void;
}

type ModalMode = 'create' | 'edit';

const ProductFeatureManagement: React.FC<ProductFeatureManagementProps> = ({ onBack }) => {
  const [features, setFeatures] = useState<ProductFeature[]>([]);
  const [pagination, setPagination] = useState<ProductFeatureListResponse['pagination']>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [codeFilter, setCodeFilter] = useState<'all' | string>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [formState, setFormState] = useState<ProductFeaturePayload>({
    code: '',
    name: '',
    description: '',
    unit: '',
    values: [],
  });
  const [selectedFeature, setSelectedFeature] = useState<ProductFeature | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'create' | 'update' | 'delete' | null;
    feature: ProductFeature | null;
    message: string;
  }>({
    visible: false,
    type: null,
    feature: null,
    message: '',
  });

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchTerm) params.search = searchTerm;
      if (codeFilter !== 'all') params.code = codeFilter;
      params.includeDeleted = showDeleted ? 'true' : 'false';

      const response = await apiService.getProductFeatures(params);
      setFeatures(response.features || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch product features:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, searchTerm, codeFilter, showDeleted]);

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedFeature(null);
    setFormState({
      code: '',
      name: '',
      description: '',
      unit: '',
      values: [],
    });
    setModalVisible(true);
  };

  const openEditModal = (feature: ProductFeature) => {
    setModalMode('edit');
    setSelectedFeature(feature);
    setFormState({
      code: feature.code,
      name: feature.name,
      description: feature.description,
      unit: feature.unit || '',
      values: feature.values?.map((v) => ({ value: v.value })) || [],
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedFeature(null);
  };

  const handleFormChange = (field: keyof ProductFeaturePayload, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleValueChange = (index: number, value: string) => {
    setFormState((prev) => {
      const newValues = [...(prev.values || [])];
      // Only update if not boolean (checkbox not checked)
      const currentValue = newValues[index]?.value || '';
      if (currentValue !== 'true' && currentValue !== 'false') {
        newValues[index] = { value };
      }
      return { ...prev, values: newValues };
    });
  };

  const handleValueBooleanChange = (index: number, checked: boolean) => {
    setFormState((prev) => {
      const newValues = [...(prev.values || [])];
      if (checked) {
        // Set to 'true' and disable number input
        newValues[index] = { value: 'true' };
      } else {
        // Reset to empty string when unchecked
        newValues[index] = { value: '' };
      }
      return { ...prev, values: newValues };
    });
  };

  const addValueRow = () => {
    setFormState((prev) => ({
      ...prev,
      values: [...(prev.values || []), { value: '' }],
    }));
  };

  const removeValueRow = (index: number) => {
    setFormState((prev) => {
      const newValues = [...(prev.values || [])];
      newValues.splice(index, 1);
      return { ...prev, values: newValues };
    });
  };

  const isValueBoolean = (value: string): boolean => {
    // Hanya 'true' yang dianggap boolean khusus (Unlimited) yang mengunci input number
    return value === 'true';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.code.trim()) {
      alert('Feature code is required');
      return;
    }
    if (!formState.name.trim()) {
      alert('Feature name is required');
      return;
    }

    const trimmedCode = formState.code.trim().toUpperCase();
    const trimmedDescription = (formState.description || '').trim();
    const trimmedUnit = (formState.unit || '').trim();

    // Normalize values:
    // - If user enters a number -> use that number (as string)
    // - If user checks Unlimited -> 'true'
    // - If user leaves it empty and doesn't check Unlimited -> 'false'
    const normalizedValues =
      formState.values && formState.values.length > 0
        ? formState.values.map((v) => {
          const raw = v?.value ?? '';
          const trimmed = raw.trim();
          if (!trimmed) {
            return { value: 'false' };
          }
          return { value: trimmed };
        })
        : [];

    const payload: ProductFeaturePayload = {
      code: trimmedCode,
      name: formState.name.trim(),
      ...(trimmedDescription && { description: trimmedDescription }),
      ...(trimmedUnit && { unit: trimmedUnit }),
      ...(normalizedValues.length > 0 && { values: normalizedValues }),
    };

    // For create, save directly without confirmation
    if (modalMode === 'create') {
      setSaving(true);
      try {
        await apiService.createProductFeature(payload);
        closeModal();
        fetchFeatures();
      } catch (error) {
        console.error('Failed to save product feature:', error);
      } finally {
        setSaving(false);
      }
      return;
    } else if (selectedFeature) {
      // Check if there are any changes
      const hasChanges = 
        formState.code.trim().toUpperCase() !== selectedFeature.code ||
        formState.name.trim() !== selectedFeature.name ||
        (formState.description || '').trim() !== (selectedFeature.description || '') ||
        (formState.unit || '').trim() !== (selectedFeature.unit || '');
      
      if (!hasChanges) {
        // No changes, just close the modal
        closeModal();
        return;
      }
      
      // Show confirmation dialog for update
      setConfirmModal({
        visible: true,
        type: 'update',
        feature: selectedFeature,
        message: `Are you sure you want to update product feature "${selectedFeature.name}"?`,
      });
      return;
    }
  };

  const confirmSave = async () => {
    if (!confirmModal.type || confirmModal.type !== 'update' || !confirmModal.feature) return;

    const trimmedCode = formState.code.trim().toUpperCase();
    const trimmedDescription = (formState.description || '').trim();
    const trimmedUnit = (formState.unit || '').trim();

    // Normalize values:
    // - If user enters a number -> use that number (as string)
    // - If user checks Unlimited -> 'true'
    // - If user leaves it empty and doesn't check Unlimited -> 'false'
    const normalizedValues =
      formState.values && formState.values.length > 0
        ? formState.values.map((v) => {
          const raw = v?.value ?? '';
          const trimmed = raw.trim();
          if (!trimmed) {
            return { value: 'false' };
          }
          return { value: trimmed };
        })
        : [];

    const payload: ProductFeaturePayload = {
      code: trimmedCode,
      name: formState.name.trim(),
      ...(trimmedDescription && { description: trimmedDescription }),
      ...(trimmedUnit && { unit: trimmedUnit }),
      ...(normalizedValues.length > 0 && { values: normalizedValues }),
    };

    setSaving(true);
    try {
      await apiService.updateProductFeature(confirmModal.feature.id, payload);
      setConfirmModal({ visible: false, type: null, feature: null, message: '' });
      closeModal();
      fetchFeatures();
    } catch (error) {
      console.error('Failed to save product feature:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (feature: ProductFeature) => {
    setConfirmModal({
      visible: true,
      type: 'delete',
      feature,
      message: `Are you sure you want to delete product feature "${feature.name}" (${feature.code})?`,
    });
  };

  const confirmDelete = async () => {
    if (!confirmModal.feature || confirmModal.type !== 'delete') return;
    
    try {
      await apiService.deleteProductFeature(confirmModal.feature.id);
      setConfirmModal({ visible: false, type: null, feature: null, message: '' });
      fetchFeatures();
    } catch (error) {
      console.error('Failed to delete feature:', error);
      alert('Failed to delete product feature. Please try again.');
    }
  };

  const handleRestore = async (feature: ProductFeature) => {
    try {
      await apiService.restoreProductFeature(feature.id);
      fetchFeatures();
    } catch (error) {
      console.error('Failed to restore feature:', error);
    }
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    setPagination((prev) => {
      const maxPage = Math.max(1, prev.totalPages || 1);
      const nextPage =
        direction === 'prev' ? Math.max(1, prev.page - 1) : Math.min(maxPage, prev.page + 1);
      return { ...prev, page: nextPage };
    });
  };

  const handlePageSizeChange = (size: number) => {
    setPagination((prev) => ({ ...prev, page: 1, limit: size }));
  };

  const stats = useMemo(() => {
    if (!features.length) {
      return { total: 0, deleted: 0 };
    }
    const deleted = features.filter((feature) => feature.softDelete).length;
    return { total: features.length, deleted };
  }, [features]);


  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCodeFilterChange = (value: 'all' | string) => {
    setCodeFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Master Data Product Feature</h1>
            <p className="text-sm text-gray-500">Maintain feature catalogue for product limits</p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          New Feature
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Features in view</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Deleted (soft)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.deleted}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Active filters</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {(codeFilter === 'all' ? 0 : 1) + (showDeleted ? 1 : 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Code Filter:</label>
            <input
              type="text"
              value={codeFilter === 'all' ? '' : codeFilter}
              onChange={(e) => {
                const value = e.target.value.trim();
                handleCodeFilterChange(value === '' ? 'all' : value);
              }}
              placeholder="Filter by code..."
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            />
          </div>
          <label className="inline-flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="mr-2"
            />
            Show deleted
          </label>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pagination.limit}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : features.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No product features found
                  </td>
                </tr>
              ) : (
                features.map((feature) => (
                  <tr key={feature.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {feature.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">{feature.name}</div>
                      <div className="text-xs text-gray-500 text-left">ID: {feature.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700 text-left">
                        {feature.unit || <span className="text-gray-400">-</span>}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-sm text-left">
                      <p className="line-clamp-2">{feature.description}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${feature.softDelete ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}
                      >
                        {feature.softDelete ? 'Deleted' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                      {new Date(feature.updatedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-left">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(feature)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {feature.softDelete ? (
                          <button
                            onClick={() => handleRestore(feature)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Restore"
                          >
                            <ArrowUturnLeftIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDelete(feature)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={pagination.page === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange('next')}
                disabled={pagination.page >= (pagination.totalPages || 1)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Create Feature' : 'Edit Feature'}
                </h2>
                <p className="text-sm text-gray-500">
                  {modalMode === 'create'
                    ? 'Add a new capability descriptor for products'
                    : 'Update existing feature definition'}
                </p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Feature Code</label>
                <input
                  type="text"
                  value={formState.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                  placeholder="e.g., CATEGORY_STORE, MAX_CONNECT_STORE"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Unique code identifier for this feature. Code must be unique.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={formState.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Unit <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formState.unit || ''}
                  onChange={(e) => handleFormChange('unit', e.target.value)}
                  placeholder="e.g., Platform, x/bln, store"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Unit will be used as prefix when displaying feature values
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <textarea
                  value={formState.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Feature Values <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={addValueRow}
                    className="text-sm text-imola-primary hover:text-yellow-600 flex items-center gap-1"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Value
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {formState.values && formState.values.length > 0 ? (
                    formState.values.map((valueItem, index) => {
                      const isBoolean = isValueBoolean(valueItem.value);
                      const isTrue = valueItem.value === 'true';
                      return (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <input
                            type="number"
                            value={
                              isTrue
                                ? ''
                                : valueItem.value === 'false'
                                  ? ''
                                  : valueItem.value
                            }
                            onChange={(e) => handleValueChange(index, e.target.value)}
                            placeholder="Enter number"
                            disabled={isBoolean}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary disabled:bg-gray-200 disabled:cursor-not-allowed"
                          />
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={isTrue}
                                onChange={(e) => {
                                  handleValueBooleanChange(index, e.target.checked);
                                }}
                                className="rounded border-gray-300 text-imola-primary focus:ring-imola-primary"
                              />
                              <span>True/Unlimited</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => removeValueRow(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">
                      No values added. Click "Add Value" to add one.
                    </p>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Add possible values for this feature. Use number input or checkbox for true/false (unlimited).
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-imola-primary text-white rounded-md text-sm font-medium hover:bg-imola-primary/80 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        visible={confirmModal.visible}
        title={
          confirmModal.type === 'update' ? 'Confirm Update' :
          confirmModal.type === 'delete' ? 'Confirm Delete' : 'Confirm'
        }
        message={confirmModal.message}
        confirmText={
          confirmModal.type === 'update' ? 'Update' :
          confirmModal.type === 'delete' ? 'Delete' : 'Confirm'
        }
        cancelText="Cancel"
        confirmButtonClass={
          confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-imola-primary hover:bg-imola-primary/80'
        }
        onConfirm={() => {
          if (confirmModal.type === 'delete') {
            confirmDelete();
          } else if (confirmModal.type === 'update') {
            confirmSave();
          }
        }}
        onCancel={() => setConfirmModal({ visible: false, type: null, feature: null, message: '' })}
      />
    </div>
  );
};

export default ProductFeatureManagement;

